import * as React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Card, Input, Typography } from 'antd';
/** Icon */
import WarningIcon from '@mui/icons-material/Warning';

import useServices from '../../../hooks/useServices';
import { ServiceNameEnum } from '../../../enums/serviceName';
import { RouteNameEnum } from '../../../enums/routeName';
import useAuth from '../../../hooks/useAuth';
import { ErrorCodeEnum } from '../../../enums/errorCode';
import { AxiosService, ErrorT } from '../../../utils/axiosService';
import MainLogo from '../../../components/MainLogo';
import AlumlyButton from '../../../components/AlumlyButton';

const { Title, Text } = Typography;

const Login: React.FC = () => {
  const { authAction } = useAuth();

  const navigate = useNavigate();

  const {
    control, setError, formState: { errors }, handleSubmit,
  } = useForm({
    defaultValues: {
      username: '',
      password: '',
    },
  });

  const { service: loginService } = useServices(ServiceNameEnum.auth.login);

  const handleError = (error: ErrorT): void => {
    switch (error.code) {
      case ErrorCodeEnum.boUser.invalidUsernameOrPassword:
        setError('username', {
          type: 'validation',
        });
        setError('password', {
          type: 'validation',
          message: error.message,
        });
        break;
      case ErrorCodeEnum.boUser.inActiveUser:
        setError('username', {
          type: 'manual',
        });
        setError('password', {
          type: 'manual',
          message: error.message,
        });
        break;
      case ErrorCodeEnum.boUser.userRoleNotFound:
        setError('username', {
          type: 'manual',
        });
        setError('password', {
          type: 'manual',
          message: error.message,
        });
        break;
      default:
        break;
    }
  };

  const onSubmit = async (formData: any): Promise<void> => {
    const { username, password } = formData;
    const { data, error } = await loginService.post({ username, password });
    if (data) {
      authAction.login(data?.accessToken, data?.user, data?.permissions);
      navigate(RouteNameEnum.dashboard.index, { replace: true });
    } else if (error) {
      const errorResponse = AxiosService.getErrorResponse(error);
      if (errorResponse) {
        handleError(errorResponse);
      }
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Card
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginTop: 48,
          marginBottom: 48,
          padding: '2rem',
          width: '100%',
          maxWidth: 500,
          boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
        }}
        styles={{ body: { width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' } }}
      >
        <div style={{ marginTop: 16 }}>
          <MainLogo size="lg" />
        </div>
        <Title
          level={4}
          style={{ marginTop: 32, marginBottom: 8, fontWeight: 'bold', textAlign: 'center' }}
        >
          BBA Admin Panel
        </Title>
        <Text type="secondary" style={{ textAlign: 'center' }}>
          Please use registered admin account to log in,
        </Text>
        <Text type="secondary" style={{ textAlign: 'center' }}>
          contact service desk at support@alumly.com
        </Text>
        <form
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          style={{
            marginTop: 16,
            width: '100%',
            maxWidth: 328,
          }}
        >
          <Text style={{ display: 'block', marginTop: 8 }}>Email</Text>
          <div style={{ position: 'relative' }}>
            <Controller
              name="username"
              control={control}
              rules={{ required: 'Email is required' }}
              render={({ field }) => (
                <Input
                  size="large"
                  placeholder="Enter email here"
                  status={errors.username ? 'error' : undefined}
                  autoFocus
                  {...field}
                />
              )}
            />
            {errors.username && (
            <span
              style={{
                position: 'absolute', bottom: -20, left: 0, color: 'red', fontSize: 14,
              }}
            >
              {errors.username?.message}
            </span>
            )}
          </div>
          <div style={{ marginTop: 32, position: 'relative' }}>
            <Text style={{ display: 'block' }}>Password</Text>
            <Controller
              name="password"
              control={control}
              rules={{ required: 'Password is required' }}
              render={({ field }) => (
                <Input.Password
                  size="large"
                  placeholder="Enter password here"
                  status={errors.password ? 'error' : undefined}
                  {...field}
                />
              )}
            />
            {errors.password?.type === 'validation' && (
            <span
              style={{
                position: 'absolute', bottom: -20, left: 0, color: 'red', fontSize: 14,
              }}
            >
              { errors.password?.message }
            </span>
            )}
          </div>
          <div style={{ marginTop: 40 }}>
            <AlumlyButton type="submit" fullWidth>Log In</AlumlyButton>
          </div>
          {errors.password?.type === 'manual' && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
              <WarningIcon color="error" />
              <span
                style={{
                  marginLeft: 8, marginTop: 'auto', color: 'red', fontSize: 14,
                }}
              >
                { errors.password?.message }
              </span>
            </div>
          )}
        </form>
      </Card>
    </div>
  );
};

export default Login;
